import React, { useEffect } from 'react';
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { DateRangeIcon } from '@material-ui/pickers/_shared/icons/DateRangeIcon';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import { storeContext } from '../provider/Provider';
import { websiteAnalyticsActions } from '../../store/website-analytics-reducer';
import {
  StyledTableCellGmb,
  StyledTableHeadCellGmb,
  StyledTablePagination,
} from '../../customUI/table/TableStyles';
import MobileTitle from '../../customUI/MobileTitle';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  toolbar: theme.mixins.toolbar,
  vmargin: {
    margin: '15px 0',
  },
  tableChange: {
    color: '#858585',
    margin: '0 5px',
  },
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 5px',
    },
  },
  colorPrimary: {
    backgroundColor: '#E5E5E5',
  },
  barColorPrimary: {
    backgroundColor: '#109CF1',
  },
  card: {
    padding: 15,
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: 5,
      margin: 10,
      marginBottom: 15,
    },
  },
  heading: {
    padding: '0 0 10px 0',
    fontSize: 16,
    fontWeight: 700,
    color: '#101010',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      padding: '0 0 5px 5px',
    },
  },
  responsiveTable: {
    overflowX: 'scroll',
  },
  flexWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  pickerWrapper: {
    position: 'relative',
    margin: '0 10px 0 0',
    [theme.breakpoints.down('sm')]: {
      margin: '10px 5px',
      width: 'calc(50% - 10px)',
    },
    '& > svg': {
      position: 'absolute',
      right: 5,
      top: 6,
      pointerEvents: 'none',
    },
    '& > .MuiFormControl-root > .MuiInputBase-root > .MuiInputBase-input': {
      padding: '10px 15px',
    },
  },
  leftSideHeading: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  searchInputFlex: {
    position: 'relative',
    marginLeft: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 0 20px 0',
    },
    '& input': {
      width: 305,
      maxWidth: '100%',
      padding: '10px 10px 10px 0',
    },
    '& svg': {
      position: 'absolute',
      right: 5,
      top: 7,
      fill: '#b6b6b6',
    },
  },
}));

const AnalyticsGoogleMyBusinessLocationsFullView = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const { state, dispatch } = React.useContext(storeContext);
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [searchRequestValue, setSearchRequestValue] = React.useState('');

  useEffect(() => {
    dispatch({
      type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_IS_FETCHING,
      payload: { isFetching: true },
    });

    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/gmb/${state.websiteSwitch.currentWebsite}`)
      .then((res) => {
        if (!res.data.error) {
          dispatch({
            type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_GMB,
            payload: {
              googleMyBusiness: res.data.data,
              isFetching: false,
            },
          });
        }
      })
      .catch();
  }, []);

  const getLocationsData = () => {
    const limit = perPage;
    const offset = page * perPage;
    const dateFrom = state.websiteAnalytics.googleMyBusinessData.start_date;
    const dateTo = state.websiteAnalytics.googleMyBusinessData.end_date;

    dispatch({
      type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_IS_FETCHING,
      payload: { isFetching: true },
    });
    let requestUrl = `${REACT_APP_FLASK_API_URL}/v1/gmb/${state.websiteSwitch.currentWebsite}/${dateFrom}/${dateTo}/${limit}/${offset}`;
    if (searchInputValue) {
      requestUrl = `${REACT_APP_FLASK_API_URL}/v1/gmb/${state.websiteSwitch.currentWebsite}/${dateFrom}/${dateTo}/${searchRequestValue}/${limit}/${offset}`;
    }
    axios
      .get(requestUrl)
      .then((res) => {
        if (!res.data.error) {
          dispatch({
            type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_GMB,
            payload: {
              googleMyBusiness: res.data.data,
              isFetching: false,
            },
          });
        }
      })
      .catch();
  };

  useEffect(getLocationsData, [page, perPage, searchRequestValue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // let searchTimeout;
  const handleChangeSearch = (event) => {
    setSearchInputValue(event.target.value);
  };

  const handleChangeSearchSubmit = (event) => {
    if (event.keyCode === 13) {
      setPage(0);
      setSearchRequestValue(searchInputValue);
    }
  };

  const handleDateChange = (type, value) => {
    [state.websiteAnalytics.googleMyBusinessData[type]] = value.toISOString().split('T');
    getLocationsData();
  };

  if (state.websiteAnalytics.isFetching) {
    return (
      <LinearProgress
        style={{ width: '100%' }}
        classes={{
          colorPrimary: classes.colorPrimary,
          barColorPrimary: classes.barColorPrimary,
        }}
      />
    );
  }

  return (
    <>
      {isPhone && <MobileTitle title="Analytics" />}

      <div className={classes.wrapper}>
        <div className={classes.card}>
          <div className={classes.flexWrapper}>
            <div className={`${classes.heading} ${classes.vmargin}`}>
              Google My Business By Location
            </div>
            <div className={classes.leftSideHeading}>
              <div className={classes.pickerWrapper}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    label="Start"
                    variant="inline"
                    inputVariant="outlined"
                    format="MMMM d, yyyy"
                    value={state.websiteAnalytics.googleMyBusinessData.start_date}
                    maxDate={state.websiteAnalytics.googleMyBusinessData.end_date}
                    onChange={(value) => handleDateChange('start_date', value)}
                    aria-label="start date picker"
                  />
                  <DateRangeIcon />
                </MuiPickersUtilsProvider>
              </div>
              <div className={classes.pickerWrapper}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    label="End"
                    variant="inline"
                    inputVariant="outlined"
                    format="MMMM d, yyyy"
                    value={state.websiteAnalytics.googleMyBusinessData.end_date}
                    maxDate={state.websiteAnalytics.googleMyBusinessData.end_date}
                    onChange={(value) => handleDateChange('end_date', value)}
                    aria-label="end date picker"
                  />
                  <DateRangeIcon />
                </MuiPickersUtilsProvider>
              </div>

              <div className={classes.searchInputFlex} id="mobileSearch">
                <TextField
                  id="search-field"
                  placeholder="Start Typing"
                  value={searchInputValue}
                  onChange={handleChangeSearch}
                  onKeyDown={handleChangeSearchSubmit}
                  aria-label="search field"
                  variant="standard"
                />
                <SearchIcon />
              </div>
            </div>
          </div>
          <div className={classes.responsiveTable}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCellGmb>Store Code</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>Website Actions</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>% ∆</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>Phone Call Actions</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>% ∆</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>Total Views</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>% ∆</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>Total Searches</StyledTableHeadCellGmb>
                  <StyledTableHeadCellGmb>% ∆</StyledTableHeadCellGmb>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.websiteAnalytics.googleMyBusinessData.locations_table.map((row) => (
                  <TableRow key={row.id}>
                    <StyledTableCellGmb component="td" scope="row">
                      {row.store_code}
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      {row.website_actions}
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      <span className={classes.tableChange}>{row.website_actions_change}%</span>
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      {row.phone_call_actions}
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      <span className={classes.tableChange}>{row.phone_call_actions_change}%</span>
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      {row.total_views}
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      <span className={classes.tableChange}>{row.total_views_change}%</span>
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      {row.total_searches}
                    </StyledTableCellGmb>
                    <StyledTableCellGmb component="td" scope="row">
                      <span className={classes.tableChange}>{row.total_searches_change}%</span>
                    </StyledTableCellGmb>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <StyledTablePagination
            rowsPerPageOptions={[15, 50]}
            component="div"
            count={state.websiteAnalytics.googleMyBusinessData.locations_total}
            rowsPerPage={perPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows:"
          />
        </div>
      </div>
    </>
  );
};

export default AnalyticsGoogleMyBusinessLocationsFullView;
